import React from "react";
import { useDispatch } from "react-redux";
import useEvent from "../hooks/useEvent.hook";
import EmbedBuyButtonCard, { Spacer } from "../components/EmbedBuyButtonCard";
import EventPublishedCard from "../components/EventPublishedCard";
import PageLoader from "../components/PageLoader";
import { PaddedPage, PageTitle } from "../components/PageLayout";
import EventLinksCard from "./EventSharingTab";
import DetailsCard, { Container } from "../elements/DetailsCard";
import { Icons } from "@sellout/ui";
import Button, { ButtonTypes } from "@sellout/ui/build/components/Button";
import styled from "styled-components";
import { Colors } from "@sellout/ui/build/Colors";
import * as AppActions from "../redux/actions/app.actions";
import { ModalTypes } from "../components/modal/Modal";
import SentEmailsTable from "../components/SentEmailsTable";
import { useQuery } from "@apollo/react-hooks";
import EMAIL_HOLDERS_HISTORY from "@sellout/models/.dist/graphql/queries/emailHoldersHistory";
import { AppNotificationTypeEnum } from "../models/interfaces/IAppNotification";
import { getErrorMessage } from "@sellout/ui/build/utils/ErrorUtil";

enum EventSharingText {
  Marketing = "Marketing",
  TextMessageMarketing = "Text Message Marketing",
  TextMessageMarketingDescription = "Click the button below to begin sending text message campaigns with Check This Out.",
  LoginToCheckThisOut = "LOGIN TO CHECK THIS OUT",
  EmailMarketing = "Email Marketing",
  EmailMarketingDescription = "Click the button below to send an email message to all current ticketholders for this event.",
  EmailTicketHolders = "EMAIL TICKETHOLDERS",
  SentEmails = "Sent Emails",
}

const Text = styled.div`
  color: ${Colors.Grey2};
  font-size: 1.4rem;
  margin-bottom: 16px;
  line-height: 1.4;
`;

const TableContainer = styled.div`
  margin-top: 16px;
`;

type EventSharingProps = {};

const EventSharing: React.FC<EventSharingProps> = () => {
  /* Hooks */
  const { event, eventId } = useEvent();
  const dispatch = useDispatch();

  const { data } = useQuery(EMAIL_HOLDERS_HISTORY, {
    fetchPolicy: "network-only",
    variables: { eventId },
    skip: !eventId,
    onError(error) {
      dispatch(
        AppActions.showNotification(
          getErrorMessage(error),
          AppNotificationTypeEnum.Error,
        ),
      );
    },
  });

  const openEmailModal = () => {
    dispatch(AppActions.pushModal(ModalTypes.EmailHoldersModal));
  };

  const openCheckThisOut = () => {
    window.open("https://app.checkthisout.io/admin", "_blank");
  };

  const handleViewEmail = (email) => {
    dispatch(
      AppActions.pushModal(ModalTypes.EmailHoldersPreviewModal, {
        viewingEmail: email,
      }),
    );
  };

  const eventTimeZone = event?.venue?.address?.timezone || "America/Denver";

  /* Render */
  return (
    <>
      <PageLoader nav sideNav fade={Boolean(event)} />
      {event && (
        <PaddedPage>
          <PageTitle>{EventSharingText.Marketing}</PageTitle>
          <EventLinksCard />
          <DetailsCard
            title={EventSharingText.TextMessageMarketing}
            headerIcon={Icons.ChatRegular}
            width="600px"
            padding="20px 20px"
          >
            <Text>{EventSharingText.TextMessageMarketingDescription}</Text>
            <Button
              type={ButtonTypes.Thin}
              text={EventSharingText.LoginToCheckThisOut}
              onClick={openCheckThisOut}
            />
          </DetailsCard>
          <DetailsCard
            title={EventSharingText.EmailMarketing}
            headerIcon={Icons.EnvelopeLight}
            width="600px"
            padding="20px 20px"
          >
            <Text>{EventSharingText.EmailMarketingDescription}</Text>
            <Button
              type={ButtonTypes.Thin}
              text={EventSharingText.EmailTicketHolders}
              onClick={openEmailModal}
            />
            {data?.emailTicketHoldersHistory.length > 0 && (
              <>
                <Spacer />
                <Container>
                  <TableContainer>
                    <SentEmailsTable
                      emails={data?.emailTicketHoldersHistory}
                      eventTimeZone={eventTimeZone}
                      onViewEmail={handleViewEmail}
                    />
                  </TableContainer>
                </Container>
              </>
            )}
          </DetailsCard>
          <EmbedBuyButtonCard event={event} />
          <EventPublishedCard event={event} />
        </PaddedPage>
      )}
    </>
  );
};

export default EventSharing;
